import { motion, useTransform, MotionValue } from "framer-motion";
import {
  Star,
  Users,
  CreditCard,
  Mail,
  Banknote,
  Calendar,
  CheckCircle,
  Sparkles,
  Clock,
  Send,
  Eye,
  MousePointer,
} from "lucide-react";
import {
  MOCK_REVIEWS_PREVIEW,
  MOCK_EMPLOYEES_PREVIEW,
  MOCK_LOYALTY_PREVIEW,
  MOCK_EMAILS_PREVIEW,
  MOCK_PAYROLL_PREVIEW,
  MOCK_SCHEDULING_PREVIEW,
} from "./mockData";

interface Step3DashboardProps {
  scrollProgress: MotionValue<number>;
}

// Feature card wrapper with animation
interface FeatureCardProps {
  scrollProgress: MotionValue<number>;
  animationStart: number;
  animationEnd: number;
  icon: React.ReactNode;
  title: string;
  colorClass: string;
  children: React.ReactNode;
}

const FeatureCard = ({
  scrollProgress,
  animationStart,
  animationEnd,
  icon,
  title,
  colorClass,
  children,
}: FeatureCardProps) => {
  const opacity = useTransform(scrollProgress, [animationStart, animationEnd], [0, 1]);
  const y = useTransform(scrollProgress, [animationStart, animationEnd], [20, 0]);

  return (
    <motion.div
      style={{ opacity, y }}
      className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4 flex flex-col min-h-[160px]"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <div className={`p-2 rounded-lg ${colorClass}`}>
          {icon}
        </div>
        <h4 className="text-base font-semibold text-foreground">{title}</h4>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {children}
      </div>
    </motion.div>
  );
};

// Reviews: Real review card with stars, author, text, source badge
const ReviewsContent = () => {
  const { latestReview, avgRating, totalReviews } = MOCK_REVIEWS_PREVIEW;

  return (
    <div className="flex flex-col h-full">
      {/* Star rating row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${
                i < latestReview.rating
                  ? "fill-amber-400 text-amber-400"
                  : "text-muted-foreground/30"
              }`}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{latestReview.timeAgo}</span>
      </div>

      {/* Author */}
      <p className="text-xs font-medium text-foreground mb-1">{latestReview.author}</p>

      {/* Review text with left border */}
      <div className="border-l-2 border-amber-400 pl-2 mb-2">
        <p className="text-[11px] text-muted-foreground line-clamp-2 italic">
          "{latestReview.text}"
        </p>
      </div>

      {/* Footer: source badge + stats */}
      <div className="flex items-center justify-between mt-auto">
        <span className="px-2 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
          {latestReview.source}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {avgRating} avg • {totalReviews} reviews
        </span>
      </div>
    </div>
  );
};

// Employees: Attendance with time, status, and breakdown badges
const EmployeesContent = () => {
  const { currentTime, businessStatus, scheduledCount, presentCount, statusBreakdown, overallStatus } = MOCK_EMPLOYEES_PREVIEW;

  const statusColor = {
    optimal: "bg-emerald-500",
    understaffed: "bg-red-500",
    issues: "bg-orange-500",
    overstaffed: "bg-blue-500",
  }[overallStatus];

  return (
    <div className="flex flex-col h-full">
      {/* Time and status row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <span className="text-base font-bold text-foreground">{currentTime}</span>
          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{businessStatus}</span>
        </div>
        <div className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
      </div>

      {/* Attendance ratio */}
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-xl font-bold text-foreground">{presentCount}</span>
        <span className="text-muted-foreground">/</span>
        <span className="text-xl font-bold text-muted-foreground">{scheduledCount}</span>
        <span className="text-xs text-muted-foreground ml-1">present</span>
      </div>

      {/* Status breakdown badges */}
      <div className="grid grid-cols-2 gap-2 mt-auto">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-[11px] text-emerald-600 dark:text-emerald-400">{statusBreakdown.onTime} on time</span>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-blue-500/10">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-[11px] text-blue-600 dark:text-blue-400">{statusBreakdown.early} early</span>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-orange-500/10">
          <div className="w-2 h-2 rounded-full bg-orange-500" />
          <span className="text-[11px] text-orange-600 dark:text-orange-400">{statusBreakdown.late} late</span>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-[11px] text-red-600 dark:text-red-400">{statusBreakdown.noShow} no-show</span>
        </div>
      </div>
    </div>
  );
};

// Loyalty: Mini pass card with gradient, points, member info
const LoyaltyContent = () => {
  const { businessName, memberName, pointsBalance, memberSince, passColor } = MOCK_LOYALTY_PREVIEW;

  return (
    <div className="h-full flex items-center justify-center">
      {/* Mini pass card */}
      <div
        className="w-full max-w-[160px] rounded-lg p-3 shadow-lg"
        style={{
          background: `linear-gradient(135deg, ${passColor} 0%, ${passColor}99 100%)`,
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 rounded bg-white/20 flex items-center justify-center">
            <span className="text-[8px] text-white font-bold">CB</span>
          </div>
          <span className="text-[11px] text-white/90 font-medium truncate">{businessName}</span>
        </div>

        {/* Points */}
        <div className="text-center mb-2">
          <p className="text-[9px] text-white/70 uppercase tracking-wider">Points</p>
          <p className="text-2xl font-bold text-white">{pointsBalance}</p>
        </div>

        {/* Member info */}
        <div className="flex justify-between items-end">
          <div>
            <p className="text-[8px] text-white/60">Member</p>
            <p className="text-[11px] text-white font-medium truncate max-w-[70px]">{memberName}</p>
          </div>
          <div className="text-right">
            <p className="text-[8px] text-white/60">Since</p>
            <p className="text-[11px] text-white">{memberSince}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Emails: Mini email preview with subject, stats
const EmailsContent = () => {
  const { latestCampaign } = MOCK_EMAILS_PREVIEW;

  return (
    <div className="flex flex-col h-full">
      {/* Mini email card */}
      <div className="bg-background/50 rounded-lg p-2.5 border border-border/50 mb-3">
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded bg-emerald-500/10 flex items-center justify-center shrink-0">
            <Send className="w-3 h-3 text-emerald-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-foreground truncate">{latestCampaign.subject}</p>
            <p className="text-[11px] text-muted-foreground truncate">{latestCampaign.preview}</p>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mt-auto">
        <div className="text-center p-1.5 rounded bg-muted/50">
          <div className="flex items-center justify-center gap-0.5 mb-1">
            <Users className="w-3 h-3 text-muted-foreground" />
          </div>
          <p className="text-xs font-medium text-foreground">{latestCampaign.sentTo}</p>
          <p className="text-[9px] text-muted-foreground">sent</p>
        </div>
        <div className="text-center p-1.5 rounded bg-emerald-500/10">
          <div className="flex items-center justify-center gap-0.5 mb-1">
            <Eye className="w-3 h-3 text-emerald-500" />
          </div>
          <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{latestCampaign.openRate}%</p>
          <p className="text-[9px] text-muted-foreground">opens</p>
        </div>
        <div className="text-center p-1.5 rounded bg-blue-500/10">
          <div className="flex items-center justify-center gap-0.5 mb-1">
            <MousePointer className="w-3 h-3 text-blue-500" />
          </div>
          <p className="text-xs font-medium text-blue-600 dark:text-blue-400">{latestCampaign.clickRate}%</p>
          <p className="text-[9px] text-muted-foreground">clicks</p>
        </div>
      </div>
    </div>
  );
};

// Payroll: Mini payslip with employee, hours, gross, net
const PayrollContent = () => {
  const { employee, hours, gross, net, status } = MOCK_PAYROLL_PREVIEW;

  return (
    <div className="flex flex-col h-full">
      {/* Employee header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <span className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400">{employee.initials}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground truncate">{employee.name}</p>
          <p className="text-[11px] text-muted-foreground">{employee.role} • {employee.hourlyRate}</p>
        </div>
        <span className={`px-2 py-0.5 text-[10px] rounded-full capitalize ${
          status === "paid"
            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            : "bg-orange-500/10 text-orange-600 dark:text-orange-400"
        }`}>
          {status}
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-2 mt-auto">
        <div className="text-center p-2 rounded bg-muted/50">
          <Clock className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs font-medium text-foreground">{hours}</p>
          <p className="text-[9px] text-muted-foreground">Hours</p>
        </div>
        <div className="text-center p-2 rounded bg-muted/50">
          <Banknote className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs font-medium text-foreground">{gross}</p>
          <p className="text-[9px] text-muted-foreground">Gross</p>
        </div>
        <div className="text-center p-2 rounded bg-emerald-500/10">
          <Banknote className="w-3.5 h-3.5 text-emerald-500 mx-auto mb-1" />
          <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{net}</p>
          <p className="text-[9px] text-muted-foreground">Net</p>
        </div>
      </div>
    </div>
  );
};

// Scheduling: Mini timeline with shift blocks
const SchedulingContent = () => {
  const { shifts, timeRange, currentTimePercent } = MOCK_SCHEDULING_PREVIEW;

  return (
    <div className="flex flex-col h-full">
      {/* Time range header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{timeRange}</span>
        <span className="text-[11px] text-orange-500 font-medium">Now</span>
      </div>

      {/* Timeline container */}
      <div className="relative flex-1 min-h-[70px]">
        {/* Background grid */}
        <div className="absolute inset-0 flex">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-1 border-l border-border/30 first:border-l-0" />
          ))}
        </div>

        {/* Current time indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-orange-500 z-10"
          style={{ left: `${currentTimePercent}%` }}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-orange-500" />
        </div>

        {/* Shift blocks */}
        <div className="relative h-full flex flex-col justify-around py-1">
          {shifts.map((shift, i) => (
            <div
              key={i}
              className="relative h-6 flex items-center"
            >
              <div
                className="absolute h-5 rounded flex items-center gap-1.5 px-2 shadow-sm"
                style={{
                  left: `${shift.startPercent}%`,
                  width: `${shift.widthPercent}%`,
                  backgroundColor: shift.color,
                }}
              >
                <div className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center shrink-0">
                  <span className="text-[8px] text-white font-medium">{shift.initials}</span>
                </div>
                <span className="text-[10px] text-white font-medium truncate hidden sm:block">{shift.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Step3Dashboard = ({ scrollProgress }: Step3DashboardProps) => {
  const successOpacity = useTransform(scrollProgress, [0.70, 0.90], [0, 1]);

  return (
    <div className="max-w-7xl mx-auto w-full h-full flex flex-col overflow-hidden py-6">
      {/* Feature Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* Reviews Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0}
            animationEnd={0.25}
            icon={<Star className="w-5 h-5 text-amber-500 dark:text-amber-400" />}
            title="Reviews"
            colorClass="bg-amber-500/10"
          >
            <ReviewsContent />
          </FeatureCard>

          {/* Employees Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0.08}
            animationEnd={0.33}
            icon={<Users className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />}
            title="Employees"
            colorClass="bg-indigo-500/10"
          >
            <EmployeesContent />
          </FeatureCard>

          {/* Loyalty Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0.16}
            animationEnd={0.41}
            icon={<CreditCard className="w-5 h-5 text-violet-500 dark:text-violet-400" />}
            title="Loyalty"
            colorClass="bg-violet-500/10"
          >
            <LoyaltyContent />
          </FeatureCard>

          {/* Emails Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0.24}
            animationEnd={0.49}
            icon={<Mail className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />}
            title="Emails"
            colorClass="bg-emerald-500/10"
          >
            <EmailsContent />
          </FeatureCard>

          {/* Payroll Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0.32}
            animationEnd={0.57}
            icon={<Banknote className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
            title="Payroll"
            colorClass="bg-emerald-600/10"
          >
            <PayrollContent />
          </FeatureCard>

          {/* Scheduling Card */}
          <FeatureCard
            scrollProgress={scrollProgress}
            animationStart={0.40}
            animationEnd={0.65}
            icon={<Calendar className="w-5 h-5 text-blue-500 dark:text-blue-400" />}
            title="Scheduling"
            colorClass="bg-blue-500/10"
          >
            <SchedulingContent />
          </FeatureCard>
      </div>

      {/* Success Message with Celebration */}
      <motion.div
        style={{ opacity: successOpacity }}
        className="mt-4 text-center shrink-0"
      >
        <div className="inline-flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/20">
          <Sparkles className="w-5 h-5 text-violet-500 dark:text-violet-400 shrink-0" />
          <div className="text-left">
            <h4 className="text-sm font-bold text-transparent bg-gradient-to-r from-violet-500 to-indigo-500 dark:from-violet-400 dark:to-indigo-400 bg-clip-text">
              All Set! Ready to Launch
            </h4>
            <p className="text-xs text-muted-foreground">
              Your business is fully integrated
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
              Done!
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
